import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getPreviewCahier, downloadExcel } from '../services/api';
import { HiOutlineDocumentDownload } from 'react-icons/hi';
import { FiFileText, FiBookOpen } from 'react-icons/fi';

function getCriticiteClass(c) {
  if (c.startsWith('4')) return 'criticite-4';
  if (c.startsWith('3')) return 'criticite-3';
  if (c.startsWith('2')) return 'criticite-2';
  return 'criticite-1';
}

// Popover flottant pour afficher le texte source PDF
function SourcePopover({ text }) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const handleToggle = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setAlignRight(window.innerWidth - rect.left < 460);
    }
    setOpen((v) => !v);
  };

  if (!text) return <span style={{ color: 'var(--gray-300)', fontSize: '0.7rem' }}>—</span>;

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={handleToggle}
        title="Voir le texte source PDF"
        style={{
          background: open ? 'var(--navy-700)' : 'var(--navy-100, #e8eef7)',
          color: open ? '#fff' : 'var(--navy-700)',
          border: '1px solid var(--navy-300, #b0c4de)',
          borderRadius: '4px',
          padding: '2px 6px',
          fontSize: '0.68rem',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          whiteSpace: 'nowrap',
        }}
      >
        <FiBookOpen style={{ fontSize: '0.75rem' }} /> Source PDF
      </button>
      {open && (
        <div style={{
          position: 'fixed',
          zIndex: 1050,
          width: '340px',
          maxWidth: 'calc(100vw - 24px)',
          background: '#1e293b',
          color: '#e2e8f0',
          borderRadius: '8px',
          padding: '12px 14px',
          fontSize: '0.78rem',
          lineHeight: '1.55',
          boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          top: ref.current ? ref.current.getBoundingClientRect().top - 8 + 'px' : '0',
          ...(alignRight
            ? { right: window.innerWidth - (ref.current ? ref.current.getBoundingClientRect().right : 0) + 'px' }
            : { left: ref.current ? ref.current.getBoundingClientRect().left + 'px' : '0' }
          ),
          transform: 'translateY(-100%)',
        }}>
          <div style={{ color: '#94a3b8', fontSize: '0.68rem', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Texte source (PDF)
          </div>
          "{text}"
        </div>
      )}
    </span>
  );
}

export default function Preview() {
  const { id } = useParams();
  const [cahier, setCahier] = useState(null);

  useEffect(() => {
    getPreviewCahier(id).then(({ data }) => setCahier(data)).catch(() => {});
  }, [id]);

  const handleDownloadExcel = async () => {
    try {
      const { data } = await downloadExcel(id);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project-${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { }
  };

  if (!cahier) return (
    <div className="loading-spinner">
      <output className="spinner-border"></output>
      <span>Chargement de la preview...</span>
    </div>
  );

  const totalTC = cahier.requirements.reduce((s, r) => s + r.test_cases.length, 0);

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Preview - {cahier.project}</h2>
          <div className="d-flex gap-2 mt-1">
            <span className="meta-tag"><FiFileText /> {cahier.requirements.length} exigences</span>
            <span className="meta-tag">{totalTC} cas de test</span>
          </div>
        </div>
        <div className="actions">
          <button onClick={handleDownloadExcel} className="btn btn-success btn-sm">
            <HiOutlineDocumentDownload /> Télécharger Excel
          </button>
        </div>
      </div>
      <div className="card">
        <div className="table-responsive">
          <table className="table table-bordered preview-table mb-0">
            <thead>
              <tr>
                <th>ID Exigence</th>
                <th>Exigence</th>
                <th>Description</th>
                <th>Type</th>
                <th>Impact MOA</th>
                <th>Probabilité MOE</th>
                <th>Criticité</th>
                <th>Source EXG</th>
                <th>ID CT</th>
                <th>Cas de Test</th>
                <th>Prérequis</th>
                <th>Action</th>
                <th>Résultat Attendu</th>
                <th>Statut</th>
                <th>Commentaire</th>
                <th>Source CT</th>
              </tr>
            </thead>
            <tbody>
              {cahier.requirements.map((req) =>
                req.test_cases.map((tc, idx) => (
                  <tr key={`${req.exg_id}-${tc.ct_id}`}>
                    {idx === 0 && (
                      <>
                        <td rowSpan={req.rowspan} className="fw-bold">{req.exg_id}</td>
                        <td rowSpan={req.rowspan}>{req.exigence}</td>
                        <td rowSpan={req.rowspan}>{req.desc_exigence}</td>
                        <td rowSpan={req.rowspan}>{req.type_exigence}</td>
                        <td rowSpan={req.rowspan}>{req.impact}</td>
                        <td rowSpan={req.rowspan}>{req.probabilite}</td>
                        <td rowSpan={req.rowspan}>
                          <span className={`criticite-badge ${getCriticiteClass(req.criticite)}`}>
                            {req.criticite}
                          </span>
                        </td>
                        <td rowSpan={req.rowspan}>
                          <SourcePopover text={req.source_text} />
                        </td>
                      </>
                    )}
                    <td className="fw-bold">{tc.ct_id}</td>
                    <td>{tc.cas_test}</td>
                    <td>{tc.prerequis}</td>
                    <td className="action-text">{tc.action}</td>
                    <td>{tc.resultat_attendu}</td>
                    <td><span className={`status-badge status-${tc.statut}`}>{tc.statut}</span></td>
                    <td className="small">{tc.commentaire || ''}</td>
                    <td>
                      <SourcePopover text={tc.source_text} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
